import {
  BlockchainNetwork,
  ConstantsHelper,
  ParsedRateLimiter,
  RENEC,
  SOL,
  U64_MAX,
  WAD,
  WalletType,
} from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"

import { ObligationType } from "@/atoms/obligations"
import { SelectedReserveType } from "@/atoms/shared"
import { NATIVE_MINT } from "@solana/spl-token"
import { ObligationDepositReserve } from "../dataTypes"
import { replaceLocaleStringWithValues } from "../helpers"
import { Configuration, calculateBorrowInterest } from "../rate"
import {
  FIAT_DECIMALS,
  RENEC_PADDING_FOR_RENT_AND_FEE,
  SOL_PADDING_FOR_RENT_AND_FEE,
  sufficientNativeTokenForTransaction,
} from "./commons"
import { calculateCompoundedInterestRate, createConfigShowingAPR } from "./utils"

const WITHDRAW_BUFFER_SLOTS = 100

export const withdrawConfigs = {
  verifyAction: (
    network: BlockchainNetwork,
    value: BigNumber,
    obligation: ObligationType | null,
    reserve: SelectedReserveType,
    wallet: WalletType,
    rateLimiter: ParsedRateLimiter | null,
    trans: any,
    currentSlot: number | null,
  ) => {
    if (value.isZero() || !value) {
      return trans.dashboard.modal.errors.please_enter_amount
    }

    if (!obligation || !currentSlot) return null
    const isRenecChain = ConstantsHelper.isRenecChain(network)
    const isSolanaChain = ConstantsHelper.isSolanaChain(network)

    const isFiatToken = reserve.decimals === FIAT_DECIMALS

    const reserveDeposited = obligation.deposits.find((d) => d.reserveAddress === reserve.address)
    if (!reserveDeposited) return null

    let { newReserveDepositedAmount } = calculateNewReserveDepositLimit(reserve, reserveDeposited, currentSlot)
    const constantBorrowLimit = obligation.minPriceBorrowLimit.minus(
      newReserveDepositedAmount.times(reserve.minPrice).times(reserve.loanToValueRatio),
    )

    let collateralWithdrawLimit = !(reserve.minPrice.isZero() || !reserve.loanToValueRatio)
      ? BigNumber.max(
          newReserveDepositedAmount.minus(
            BigNumber.max(
              BigNumber(0),
              obligation?.totalBorrowValue
                .minus(constantBorrowLimit)
                .dividedBy(reserve.minPrice.times(reserve.loanToValueRatio)),
            ),
          ),
          0,
        )
      : new BigNumber(U64_MAX)

    const roundingStrategy = isFiatToken ? BigNumber.ROUND_UP : undefined

    newReserveDepositedAmount = newReserveDepositedAmount.decimalPlaces(reserve.decimals, roundingStrategy)
    collateralWithdrawLimit = collateralWithdrawLimit.decimalPlaces(reserve.decimals, roundingStrategy)

    const reserveRateLimit =
      reserve.rateLimiter.remainingOutflow?.dividedBy(new BigNumber(10 ** reserve.decimals)) ?? new BigNumber(U64_MAX)

    const poolRateLimit = rateLimiter?.remainingOutflow?.dividedBy(reserve.maxPrice) ?? new BigNumber(U64_MAX)

    const valueRounded = value.decimalPlaces(reserve.decimals, roundingStrategy)

    const amountPadding = isRenecChain ? RENEC_PADDING_FOR_RENT_AND_FEE : SOL_PADDING_FOR_RENT_AND_FEE
    const isSolanaInSufficient = isSolanaChain && !sufficientNativeTokenForTransaction(network, wallet)
    const nativeMintBalance =
      wallet.find((ass) => ass.mintAddress === NATIVE_MINT.toBase58())?.amount ?? new BigNumber(0)
    const minNativeFee = isSolanaChain ? new BigNumber(amountPadding).minus(nativeMintBalance).toString() : null

    if (valueRounded.isGreaterThan(reserve.availableAmount.decimalPlaces(reserve.decimals, roundingStrategy))) {
      return trans.dashboard.modal.errors.insufficient_liquidity_withdraw
    }
    if (valueRounded.isGreaterThan(newReserveDepositedAmount.decimalPlaces(reserve.decimals, roundingStrategy))) {
      return trans.dashboard.modal.errors.cannot_withdraw_limit
    }
    if (valueRounded.isGreaterThan(collateralWithdrawLimit.decimalPlaces(reserve.decimals, roundingStrategy))) {
      return trans.dashboard.modal.errors.cannot_withdraw_under_collateralization
    }
    if (valueRounded.isGreaterThan(poolRateLimit.decimalPlaces(reserve.decimals, roundingStrategy))) {
      return trans.dashboard.modal.errors.pool_outflow_rate_limit
    }
    if (valueRounded.isGreaterThan(reserveRateLimit.decimalPlaces(reserve.decimals, roundingStrategy))) {
      return trans.dashboard.modal.errors.reserve_outflow_rate_limit
    }
    if (isSolanaInSufficient && minNativeFee) {
      return replaceLocaleStringWithValues(
        trans.dashboard.modal.errors.min_native_fee,
        minNativeFee,
        isRenecChain ? RENEC : SOL,
      )
    }
    return null
  },
  getNewCalculations: (obligation: ObligationType | null, reserve: SelectedReserveType, value: string) => {
    if (!obligation) {
      return {
        borrowLimit: null,
        newBorrowLimit: null,
        utilization: null,
        newBorrowUtilization: null,
        calculatedBorrowFee: null,
      }
    }

    const valueObj = new BigNumber(value)
    const newBorrowLimit = !valueObj.isNaN()
      ? obligation.minPriceBorrowLimit.minus(valueObj.times(reserve.minPrice).times(reserve.loanToValueRatio))
      : null
    const utilization = !obligation.totalSupplyValue.isZero()
      ? obligation.totalBorrowValue.dividedBy(obligation.totalSupplyValue)
      : null
    const newTotalSupply = !valueObj.isNaN()
      ? BigNumber.max(obligation.totalSupplyValue.minus(valueObj.times(reserve.minPrice)), new BigNumber(0.0001))
      : obligation.totalSupplyValue
    const newBorrowUtilization =
      newTotalSupply && !newTotalSupply.isZero() ? obligation.totalBorrowValue.dividedBy(newTotalSupply) : utilization

    return {
      borrowLimit: obligation.minPriceBorrowLimit,
      newBorrowLimit: newBorrowLimit,
      utilization: utilization,
      newBorrowUtilization,
      calculatedBorrowFee: null,
    }
  },
  calculateMax: (
    reserve: SelectedReserveType,
    _wallet: WalletType,
    obligation: ObligationType | null,
    rateLimiter: ParsedRateLimiter | null,
    currentSlot: number | null,
  ) => {
    if (!obligation || !currentSlot) {
      return new BigNumber(0)
    }

    const isFiatToken = reserve.decimals === FIAT_DECIMALS

    const reserveDeposited = obligation.deposits.find((d) => d.reserveAddress === reserve.address)
    if (!reserveDeposited) return BigNumber(0)

    let { newReserveDepositedAmount } = calculateNewReserveDepositLimit(reserve, reserveDeposited, currentSlot)
    const constantBorrowLimit = obligation.minPriceBorrowLimit.minus(
      newReserveDepositedAmount.times(reserve.minPrice).times(reserve.loanToValueRatio),
    )

    let collateralWithdrawLimit = !(reserve.price.isZero() || !reserve.loanToValueRatio)
      ? newReserveDepositedAmount.minus(
          BigNumber.max(
            BigNumber(0),
            obligation.totalBorrowValue
              .minus(constantBorrowLimit)
              .dividedBy(reserve.minPrice.times(reserve.loanToValueRatio)),
          ),
        )
      : new BigNumber(U64_MAX)

    const reserveRateLimit =
      reserve.rateLimiter.remainingOutflow?.dividedBy(new BigNumber(10 ** reserve.decimals)) ?? new BigNumber(U64_MAX)

    const poolRateLimit = rateLimiter?.remainingOutflow?.dividedBy(reserve.maxPrice) ?? new BigNumber(U64_MAX)

    const roundingStrategy = isFiatToken ? BigNumber.ROUND_UP : undefined

    newReserveDepositedAmount = newReserveDepositedAmount.decimalPlaces(reserve.decimals, roundingStrategy)
    collateralWithdrawLimit = collateralWithdrawLimit.decimalPlaces(reserve.decimals, roundingStrategy)

    return BigNumber.max(
      BigNumber.min(
        newReserveDepositedAmount,
        collateralWithdrawLimit,
        reserve.availableAmount,
        reserveRateLimit,
        poolRateLimit,
      ),
      new BigNumber(0),
    )
  },
}

export const calculateNewReserveDepositLimit = (
  reserve: SelectedReserveType,
  reserveDeposited: ObligationDepositReserve,
  currentSlot: number,
) => {
  const configShowingAPR: Configuration = createConfigShowingAPR(reserve)
  const currentBorrowRate = calculateBorrowInterest(configShowingAPR)
  const slotElapsed = currentSlot - (reserve.lastUpdateSlot.toNumber() ?? 0) + WITHDRAW_BUFFER_SLOTS
  const compoundedInterestRateWhenRefreshReserve = calculateCompoundedInterestRate(currentBorrowRate, slotElapsed)
  const reserveBorrowedAmount = new BigNumber(reserve.totalBorrowWad.toString()).div(WAD)
  const netNewDebt = reserveBorrowedAmount
    .multipliedBy(compoundedInterestRateWhenRefreshReserve)
    .minus(reserveBorrowedAmount)
  const newBorrowedAmountAfterRefresh = reserveBorrowedAmount.plus(netNewDebt)
  const accumulatedProtocolFees = new BigNumber(reserve.accumulatedProtocolFeesWad.toString()).div(WAD)
  const newAccumulatedProtocolTakeFee = netNewDebt
    .multipliedBy(reserve.interestRateSpread)
    .plus(accumulatedProtocolFees)

  const availableAmount = new BigNumber(reserve.availableAmountWad.toString())
  const totalLiquidity = availableAmount.plus(newBorrowedAmountAfterRefresh).minus(newAccumulatedProtocolTakeFee)
  const mintTotalSupply = reserve.mintTotalSupplyWad
  const newCTokenExchangeRate = new BigNumber(totalLiquidity.div(new BigNumber(mintTotalSupply.toString())))
  const newReserveDepositedAmount = reserveDeposited.depositedAmountDecimals.multipliedBy(newCTokenExchangeRate)
  return { newReserveDepositedAmount }
}
